import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Input, InputNumber, Popconfirm, Row, Switch, Tooltip } from 'antd';
import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Panel from '../../../../shared/components/card/Panel';
import PageHeader from '../../../../shared/components/pageHeader/PageHeader';
import {
  APPLICATION_ID, authFeatureConstants, DEFAULT_PAGE_SIZE,
  GUTTOR_SPACE, INVENTORY_MESSAGE_CONSTANTS, INVENTROY_CONSTANTS, PAGE_QUERY_PARAM, REQUEST_PAYLOAD_FOR_ROOM, TOOLTIP_TEXT,
} from '../../../../shared/constants/commonConstants';
import { INVENTORY_FETCH_IP_PACKAGE, INVENTORY_SAVE_IP_PACKAGE } from '../../../../shared/constants/urlEndPoints';
import { FetchInPatientPackageRequest, FetchInPatientPackageResponse, SaveInPatientPackageRequest, SaveInPatientPackageResponse } from '../../../../shared/models/inPatient.model';
import SendRequest from '../../../../shared/services/sendRequest.service';
import { fetchHospitalProfile } from '../../../../store/actions/hospitalAction';
import { fetchIPInclusionMaster } from '../../../../store/actions/inventoryAction';
import { ReactComponent as EditIcon } from '../../../../assets/images/svg/i_edit.svg';
import {
  base64UrlDecode, base64UrlEncode, CalculateDiscountPercentage, canActivate, error, extractSecondLevelBaseUrl,
  formatPrice, getBulletPoints, GetFirstElement, success,
} from '../../../../utils/utility';
import './IPPackageDetail.less';

const IPPackageDetail = ({ match: { path, params: { id: packageId } } }: any): ReactElement => {
  const pageNumber: any = new URLSearchParams(useLocation().search).get('page');
  const [packageDetail, setPackageDetail] = useState(null as any);
  const [isChecked, setIsChecked] = useState(true);
  const history = useHistory();
  const dispatch = useDispatch();
  const isAuthorized = canActivate(authFeatureConstants.EDIT_IP_PACKAGES);
  const id = base64UrlDecode(packageId);
  const inclusionMasterList = useSelector(
    ({ inventory: { inPatientinclusionMaster } }: any): any => inPatientinclusionMaster,
  );
  const storedHospitalProfile = useSelector(
    ({ hospital: { hospitalProfile } }: any): any => hospitalProfile,
  );
  useEffect((): void => {
    if (!inclusionMasterList) {
      dispatch(fetchIPInclusionMaster());
    }
    if (!storedHospitalProfile) {
      dispatch(fetchHospitalProfile(REQUEST_PAYLOAD_FOR_ROOM));
    }
  }, []);
  useEffect((): void => {
    const reqpayload: FetchInPatientPackageRequest = {
      fetchInActiveRecord: true,
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      packageById: true,
      ids: [Number(id)],
      packageType: INVENTROY_CONSTANTS.packageType_InPatient,
    };
    fetchPackageDetail(reqpayload);
  }, [id]);

  const getRoomAliases = (typeId: number): string[] => {
    let aliases = [] as string[];
    if (typeId) {
      aliases = GetFirstElement(storedHospitalProfile)
        ?.rooms
        ?.filter((x: any): any => x.typeId === typeId)
        ?.map((x: any): any => x.roomAlias);
    }
    return aliases;
  };

  const getPricingList = (): any => {
    if (packageDetail && packageDetail?.pricingDetails && packageDetail?.pricingDetails.length > 0
      && storedHospitalProfile && GetFirstElement(storedHospitalProfile).rooms
      && GetFirstElement(storedHospitalProfile)?.rooms?.length > 0) {
      return packageDetail?.pricingDetails
        .filter((x: any): any => GetFirstElement(storedHospitalProfile)?.rooms.some((y: any): any => x.typeId === y.typeId))
        ?.map((pricing: any): any => {
          return (<Row gutter={GUTTOR_SPACE} key={pricing?.id} className="mb-md">
            <Col md={8}>
              <p className="label-title">
                Room type
                <Tooltip title={
                  <div dangerouslySetInnerHTML=
                    {{ __html: getBulletPoints(getRoomAliases(pricing?.typeId)) }} />}
                  placement="rightTop" overlayClassName="custom-tooltip-row-content"
                >
                  &nbsp;<InfoCircleOutlined />
                </Tooltip>
              </p>
              <Input value={pricing?.typeName} disabled={true} />
            </Col>
            <Col md={6}>
              <p className="label-title">Price</p>
              <InputNumber value={pricing?.price}
                formatter={(value: any): any => formatPrice(Number(value), false)}
                disabled={true} />
            </Col>
            <Col md={4}>
              <p className="label-title">Discount</p>
              <Input type="number"
                value={CalculateDiscountPercentage(pricing?.price, pricing?.discountedPrice)}
                disabled={true} suffix="%" />
            </Col>
            <Col md={6}>
              <InputNumber style={{ marginTop: 19.2 }}
                formatter={(value: any): any => formatPrice(Number(value), false)}
                value={pricing?.discountedPrice} disabled={true} />
            </Col>
          </Row>);
        });
    }
  };
  const fetchPackageDetail = async (
    payload: any,
  ): Promise<any> => {
    const url = process.env.REACT_APP_INVENTORY_API_BASE_URL + INVENTORY_FETCH_IP_PACKAGE;
    const result: FetchInPatientPackageResponse = await SendRequest.instance.post<
      FetchInPatientPackageRequest,
      FetchInPatientPackageResponse
    >(url, payload);
    if (result && result?.isSuccess && result?.packages && result?.packages?.length > 0) {
      setPackageDetail(GetFirstElement(result?.packages));
      setIsChecked(GetFirstElement(result?.packages)?.isActive);
    } else {
      error(INVENTORY_MESSAGE_CONSTANTS.tryAgain);
    }
  };
  const onEditProfile = (): void => {
    history.push(`${extractSecondLevelBaseUrl(path) + INVENTROY_CONSTANTS.editIpPackageRoute + base64UrlEncode(id)}${PAGE_QUERY_PARAM + (pageNumber ? pageNumber : 1)}`);
  };

  const onDeletePackage = async (): Promise<void> => {
    const payload: SaveInPatientPackageRequest = {
      applicationId: APPLICATION_ID,
      package: {
        ...packageDetail, isActive: !isChecked, inclusionList: [], pricingList: [],
      },
    };
    const url = process.env.REACT_APP_INVENTORY_API_BASE_URL + INVENTORY_SAVE_IP_PACKAGE;
    const result: SaveInPatientPackageResponse = await SendRequest.instance.post<
      SaveInPatientPackageRequest,
      SaveInPatientPackageResponse
    >(url, payload);
    if (result && result.isSuccess) {
      if (isChecked) {
        success(INVENTORY_MESSAGE_CONSTANTS.isDeactivatedSuccess);
      } else {
        success(INVENTORY_MESSAGE_CONSTANTS.isActivatedSuccess);
      }
      setIsChecked(!isChecked);
    } else {
      error(INVENTORY_MESSAGE_CONSTANTS.tryAgain);
    }
  };
  const getSelectedInclusion = (): string[] => {
    return packageDetail?.inclusions ? packageDetail?.inclusions
      .filter((x: any): boolean => x.isActive)
      .map((x: any): string => {
        return x.inclusionName;
      }) : null;
  };

  return (
    <div>
      <PageHeader
        colSize={18}
        pageTitle={packageDetail?.procedureName}
        backUrl={extractSecondLevelBaseUrl(path) + INVENTROY_CONSTANTS.ipPackagesRoute + PAGE_QUERY_PARAM + (pageNumber ? pageNumber : 1)}
        rightComponent={
          (): ReactElement =>
            <Row gutter={GUTTOR_SPACE}>
              <Col md={24} className="text-right">
                <div className="text-right">
                  {
                    isAuthorized &&
                    <Tooltip title={TOOLTIP_TEXT.edit} placement={'bottom'}>
                      <Button className="v-align-sub" type={'text'} onClick={onEditProfile} >
                        <EditIcon className="fill-primary" />
                      </Button>
                    </Tooltip>
                  }
                  <Popconfirm placement={'topLeft'}
                    disabled={!isAuthorized}
                    title={!isChecked ? INVENTORY_MESSAGE_CONSTANTS.isActivatePackage : INVENTORY_MESSAGE_CONSTANTS.isDeActivatePackage}
                    okText="Yes"
                    cancelText="No"
                    onConfirm={onDeletePackage}
                  >
                    <Tooltip placement={'bottom'} title={isChecked ? TOOLTIP_TEXT.active : TOOLTIP_TEXT.inActive}>
                      <Button type={'text'}>
                        <Switch size="small" checked={isChecked} disabled={!isAuthorized} />
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                </div>
              </Col>
            </Row>}
      />
      {packageDetail &&
        <div className="ant-tabs-content-holder tab-custom-container">
          <Row gutter={GUTTOR_SPACE}>
            <Col md={16}>
              <Panel title={INVENTORY_MESSAGE_CONSTANTS.title.packageDetails} >
                <div className="label-value mb-0">{packageDetail?.procedureName}</div>
                <p className="text-elipsis"
                  style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: packageDetail?.description }} />
                <Row gutter={GUTTOR_SPACE}>
                  <Col md={8}>
                    <p className="label-title">Department</p>
                    <div className="label-value mb-0">{packageDetail?.departmentName}</div>
                  </Col>
                  <Col md={8}>
                    <p className="label-title">Procedure name</p>
                    <div className="label-value mb-0">{packageDetail?.procedureName}</div>
                  </Col>
                  <Col md={8}>
                    <p className="label-title">Length of stay</p>
                    <div className="label-value mb-0">{packageDetail?.lengthOfStay}</div>
                  </Col>
                </Row>
              </Panel>
              <Panel title={INVENTORY_MESSAGE_CONSTANTS.title.pricingDetails}>
                {getPricingList()}
              </Panel>
            </Col>
            {inclusionMasterList &&
              <Col md={8}>
                <Panel title="Inclusions">
                  <Checkbox.Group
                    className="inclusion-checkbox"
                    options={getSelectedInclusion()}
                    value={getSelectedInclusion()}
                  />
                </Panel>
              </Col>}
          </Row>
        </div>
      }
    </div>
  );
};

export default IPPackageDetail;
